import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { RemotionGraphic } from '../components/graphics/RemotionGraphics';

const { FiHeart, FiUsers, FiHome, FiClock, FiShield, FiSun, FiMoon, FiActivity, FiArrowRight, FiCheck, FiShieldCheck } =
  FiIcons;

const sectionReveal = {
  hidden: { opacity: 0, y: 28 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.16, 1, 0.3, 1],
      when: 'beforeChildren',
      staggerChildren: 0.08
    }
  }
};

const featureReveal = {
  hidden: { opacity: 0, x: -12 },
  show: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.45,
      ease: 'easeOut'
    }
  }
};

const Services = () => {
  const mainServices = [
    {
      icon: FiHeart,
      graphic: 'services-personal',
      title: "Personal Care",
      description: "Compassionate assistance with personal hygiene, dressing, grooming, and mobility support tailored to individual needs.",
      features: [
        'Washing and bathing support with dignity-focused routines',
        'Dressing, grooming, and comfort assistance',
        'Toileting and continence support plans',
        'Safe transfer and mobility guidance',
        'Medication prompts and routine reminders',
        'Private, person-centered care plans'
      ],
      gradient: 'from-emerald-50 to-blue-50'
    },
    {
      icon: FiUsers,
      graphic: 'services-companion',
      title: "Companionship Care",
      description: "Social interaction and emotional support to reduce isolation and promote mental wellbeing and quality of life.",
      features: [
        'Conversation, engagement, and activity support',
        'Walks to appointments and social outings',
        'Meal prep and nutrition companionship',
        'Light household support with respect',
        'Shopping and errands when needed',
        'Hobby-based routines for daily joy'
      ],
      gradient: 'from-sky-50 to-indigo-50'
    },
    {
      icon: FiShield,
      graphic: 'services-dementia',
      title: "Dementia Care",
      description: "Specialized care for individuals with dementia, providing safe, structured support that maintains dignity and comfort.",
      features: [
        'Personalized memory routines and orientation cues',
        'Safety-first environment and check-ins',
        'Behavioral support with evidence-based practice',
        'Family-led communication and updates',
        'Cognitive stimulation and soothing activities',
        'Dignity-first escalation planning'
      ],
      gradient: 'from-violet-50 to-fuchsia-50'
    },
    {
      icon: FiHome,
      graphic: 'services-livein',
      title: "Live-in Care",
      description: "24-hour care in the comfort of your own home, providing peace of mind and continuous support when needed.",
      features: [
        'Care continuity and routine consistency',
        'Night and day support from a familiar carer',
        'Early warning checks and response protocols',
        'Shared communication notes with family',
        'Household support to sustain stability',
        'Overnight comfort and reassurance plans'
      ],
      gradient: 'from-teal-50 to-emerald-50'
    },
    {
      icon: FiClock,
      graphic: 'services-respite',
      title: "Respite Care",
      description: "Temporary care services that give family caregivers a well-deserved break while ensuring continued quality care.",
      features: [
        'Short-term care cover with seamless handovers',
        'Flexible morning, weekend, and holiday options',
        'Trusted backup during family stress periods',
        'Emergency respite with rapid response',
        'Planned rest periods designed around your family',
        'Supportive follow-up and family debrief'
      ],
      gradient: 'from-cyan-50 to-blue-50'
    },
    {
      icon: FiActivity,
      graphic: 'services-rehab',
      title: "Rehabilitation Support",
      description: "Post-hospital care and rehabilitation support to help clients recover safely and regain independence at home.",
      features: [
        'Post-surgery recovery routines and monitoring',
        'Physiotherapy-aligned mobility support',
        'Medication timing and symptom tracking',
        'Goal-based functional exercises',
        'Fall prevention and home safety checks',
        'Coordinated hospital discharge follow-through'
      ],
      gradient: 'from-lime-50 to-emerald-50'
    }
  ];

  const additionalServices = [
    {
      icon: FiSun,
      graphic: 'services-day',
      title: "Day Care Services",
      description: "Structured daytime activities and care in a safe, social environment."
    },
    {
      icon: FiMoon,
      graphic: 'services-night',
      title: "Night Care",
      description: "Overnight support and monitoring for safety and peace of mind."
    },
    {
      icon: FiShield,
      graphic: 'services-palliative',
      title: "Palliative Care",
      description: "Compassionate end-of-life care focused on comfort and dignity."
    },
    {
      icon: FiShieldCheck,
      graphic: 'services-family-support',
      title: "Family Support",
      description: "Guidance and support for families navigating care decisions."
    }
  ];

  const careProcess = [
    {
      step: "1",
      title: "Initial Consultation",
      description: "We begin with a comprehensive assessment of your care needs, preferences, and circumstances."
    },
    {
      step: "2",
      title: "Care Plan Development",
      description: "A personalized care plan is created, outlining specific services, schedules, and care objectives."
    },
    {
      step: "3",
      title: "Carer Matching",
      description: "We carefully match you with qualified carers who meet your specific needs and preferences."
    },
    {
      step: "4",
      title: "Care Delivery",
      description: "Our trained professionals provide consistent, high-quality care according to your personalized plan."
    },
    {
      step: "5",
      title: "Ongoing Monitoring",
      description: "Regular reviews ensure care quality and adapt services as needs change over time."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Home Care Services - KCG | Personal Care, Dementia Care, Live-in Care</title>
        <meta name="description" content="Comprehensive home care services including personal care, dementia care, live-in care, and companionship. Professional, compassionate care tailored to your needs." />
        <meta name="keywords" content="home care services, personal care, dementia care, live-in care, companionship care, respite care, UK healthcare" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary-50 via-white to-secondary-50 py-20">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            animate="show"
            variants={sectionReveal}
            transition={{ duration: 0.8 }}
            className="grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-12 items-center"
          >
            <div className="text-center lg:text-left">
              <p className="text-sm font-medium uppercase tracking-[0.3em] text-primary-900/80">
                Home-based care, thoughtfully designed
              </p>
              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mt-4 mb-6">
                Our <span className="text-primary-900">Care Services</span>
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto lg:mx-0 leading-relaxed">
                We offer a comprehensive range of professional care services designed to support
                independence, dignity, and quality of life in the comfort of your own home.
              </p>
            </div>
            <motion.div
              variants={sectionReveal}
              whileHover={{ scale: 1.015 }}
              transition={{ type: 'spring', stiffness: 140, damping: 18 }}
              className="rounded-[2rem] overflow-hidden border border-primary-100 bg-white shadow-xl"
            >
              <RemotionGraphic
                variant="services-hero"
                className="aspect-[900/520] w-full"
                ariaLabel="Animated overview of KCG service options"
              />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {mainServices.map((service, index) => (
              <motion.div
                key={service.title}
                initial="hidden"
                whileInView="show"
                variants={sectionReveal}
                whileHover={{ y: -6 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className={`grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-10 xl:gap-16 items-center rounded-[2rem] border border-slate-100 bg-slate-50/80 p-8 shadow-sm ring-1 ring-white/60 ${
                  index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
                }`}
              >
                <div className={`space-y-6 ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                  <div className="flex items-center space-x-4">
                    <div className="relative w-12 h-12 rounded-xl bg-white shadow-sm border border-primary-100 flex items-center justify-center">
                      <SafeIcon icon={service.icon} className="w-6 h-6 text-primary-900" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900">{service.title}</h2>
                  </div>
                  
                  <p className="text-lg text-gray-600 leading-relaxed">
                    {service.description}
                  </p>

                  <div className="space-y-3">
                    <h3 className="text-lg font-semibold text-gray-900">What's Included:</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {service.features.map((feature, featureIndex) => (
                        <motion.div
                          key={featureIndex}
                          variants={featureReveal}
                          className="flex items-center space-x-2"
                        >
                          <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-600" />
                          <span className="text-gray-600">{feature}</span>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  <Link
                    to="/request-care"
                    className="inline-flex items-center text-primary-900 hover:text-primary-700 font-semibold group"
                  >
                    Request This Service
                    <SafeIcon icon={FiArrowRight} className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>

                <motion.div
                  className={index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}
                  variants={sectionReveal}
                  whileHover={{ scale: 1.02 }}
                  transition={{ type: 'spring', stiffness: 140, damping: 18 }}
                >
                  <div className={`relative overflow-hidden rounded-[1.4rem] bg-gradient-to-br ${service.gradient} p-4 shadow-sm`}>
                    <RemotionGraphic
                      variant={service.graphic}
                      className="relative aspect-[16/13] w-full"
                      ariaLabel={`${service.title} care illustration`}
                    />
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="show"
            variants={sectionReveal}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Additional Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We also offer specialized services to meet unique care needs and circumstances.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {additionalServices.map((service, index) => (
              <motion.div
                key={service.title}
                initial="hidden"
                whileInView="show"
                variants={sectionReveal}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -4, scale: 1.01 }}
                className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 border border-slate-100"
              >
                <div className="w-24 h-24 md:w-28 md:h-28 mb-6 rounded-2xl overflow-hidden bg-primary-50 border border-primary-100">
                  <RemotionGraphic
                    variant={service.graphic}
                    className="w-full h-full"
                    ariaLabel={`${service.title} support visual`}
                  />
                </div>
                <div className="mb-2 flex items-center gap-3">
                  <SafeIcon icon={service.icon} className="w-4 h-4 text-primary-900" />
                  <h3 className="text-lg font-semibold text-gray-900">{service.title}</h3>
                </div>
                <p className="text-gray-600">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Care Process */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="show"
            variants={sectionReveal}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Care Process</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We follow a structured approach to ensure you receive the best possible care tailored to your needs.
            </p>
          </motion.div>

          <div className="space-y-8">
            {careProcess.map((step, index) => (
              <motion.div
                key={step.step}
                initial="hidden"
                whileInView="show"
                variants={sectionReveal}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className={`flex flex-col md:flex-row items-center ${
                  index % 2 === 0 ? '' : 'md:flex-row-reverse'
                } gap-8`}
              >
                <div className="flex-1">
                  <div className={`bg-gray-50 p-8 rounded-xl ${
                    index % 2 === 0 ? 'text-left' : 'md:text-right'
                  }`}>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{step.title}</h3>
                    <p className="text-lg text-gray-600 leading-relaxed">{step.description}</p>
                  </div>
                </div>
                
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-primary-900 rounded-full flex items-center justify-center">
                    <span className="text-2xl font-bold text-white">{step.step}</span>
                  </div>
                </div>
                
                <div className="hidden md:block flex-1">
                  {/* Empty space for alternating layout */}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Quality Assurance */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial="hidden"
              whileInView="show"
              variants={sectionReveal}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <RemotionGraphic
                variant="services-quality"
                className="aspect-[16/13] w-full rounded-2xl shadow-lg overflow-hidden"
                ariaLabel="Animated quality assurance care visual"
              />
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="show"
              variants={sectionReveal}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h2 className="text-4xl font-bold text-gray-900">Quality Assurance</h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                We maintain the highest standards of care through rigorous quality assurance processes 
                and continuous improvement initiatives.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-600 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Regular Training</h3>
                    <p className="text-gray-600">All staff receive ongoing training and professional development</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-600 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Care Monitoring</h3>
                    <p className="text-gray-600">Regular reviews and assessments ensure care quality</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-600 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Client Feedback</h3>
                    <p className="text-gray-600">We actively seek and respond to client and family feedback</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-600 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Regulatory Compliance</h3>
                    <p className="text-gray-600">See the current CQC listing linked in our footer for the North Tyneside service.</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <h2 className="text-4xl font-bold text-white mb-4">Ready to Get Started?</h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Contact us today for a free consultation and let us help you find the perfect care solution.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/request-care"
                className="inline-flex items-center justify-center bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors duration-200"
              >
                Request Care Assessment
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors duration-200"
              >
                Contact Us Today
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Services;
